import { RefObject, useEffect } from "react";

const useClickOutside = (
    ref: RefObject<HTMLElement | null>,
    callback: () => void,
    ignoreClass = "ignore-click-outside",
) => {
    useEffect(() => {
        // A text-selection drag dispatches its `click` on the closest common
        // ancestor of the mousedown and mouseup, not on the element where the
        // gesture started: remember the mousedown to ignore the click it yields.
        let startedInside = false;

        const isIgnored = (target: HTMLElement) =>
            target.localName === "html" ||
            Boolean(target.closest(`.${ignoreClass}`)) ||
            Boolean(ref.current?.contains(target));

        const handleMouseDown = (event: MouseEvent) => {
            startedInside = isIgnored(event.target as HTMLElement);
        };

        const handleClick = (event: MouseEvent) => {
            const ignored =
                startedInside || isIgnored(event.target as HTMLElement);
            startedInside = false;

            if (!ignored) {
                callback();
            }
        };

        document.addEventListener("mousedown", handleMouseDown, true);
        document.addEventListener("click", handleClick, true); // true = capture phase

        return () => {
            document.removeEventListener("mousedown", handleMouseDown, true);
            document.removeEventListener("click", handleClick, true);
        };
    }, [ref, callback, ignoreClass]);
};

export default useClickOutside;
